import React from "react";
import { Button, Container, Divider, Icon } from "semantic-ui-react";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import "react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css";
import moment from "moment";

export interface MetricPeriod {
  dateFrom: string | null;
  dateTo: string | null;
}

interface Props {
  value: MetricPeriod;
  onChange: (value: MetricPeriod) => void;
}

type PresetKey = "7d" | "30d" | "90d" | "ytd" | "all";

interface Preset {
  key: PresetKey;
  label: string;
  compute: () => MetricPeriod;
}

const startOfDayIso = (m: moment.Moment) => m.startOf("day").toISOString();
const endOfDayIso = (m: moment.Moment) => m.endOf("day").toISOString();

const PRESETS: Preset[] = [
  {
    key: "7d",
    label: "Last 7 days",
    compute: () => ({ dateFrom: startOfDayIso(moment().subtract(6, "days")), dateTo: endOfDayIso(moment()) }),
  },
  {
    key: "30d",
    label: "Last 30 days",
    compute: () => ({ dateFrom: startOfDayIso(moment().subtract(29, "days")), dateTo: endOfDayIso(moment()) }),
  },
  {
    key: "90d",
    label: "Last 90 days",
    compute: () => ({ dateFrom: startOfDayIso(moment().subtract(89, "days")), dateTo: endOfDayIso(moment()) }),
  },
  {
    key: "ytd",
    label: "This year",
    compute: () => ({ dateFrom: startOfDayIso(moment().startOf("year")), dateTo: endOfDayIso(moment()) }),
  },
  {
    key: "all",
    label: "All time",
    compute: () => ({ dateFrom: null, dateTo: null }),
  },
];

/**
 * Returns the preset whose computed range matches the active value, or null when
 * the active range is custom. Compared on day granularity to ignore time drift.
 */
const matchPreset = (value: MetricPeriod): PresetKey | null => {
  for (const preset of PRESETS) {
    const candidate = preset.compute();
    const sameFrom =
      (candidate.dateFrom === null && value.dateFrom === null) ||
      (!!candidate.dateFrom && !!value.dateFrom && moment(candidate.dateFrom).isSame(value.dateFrom, "day"));
    const sameTo =
      (candidate.dateTo === null && value.dateTo === null) ||
      (!!candidate.dateTo && !!value.dateTo && moment(candidate.dateTo).isSame(value.dateTo, "day"));
    if (sameFrom && sameTo) {
      return preset.key;
    }
  }
  return null;
};

export const MetricPeriodSelector = ({ value, onChange }: Props) => {
  const activePreset = matchPreset(value);

  const handleCustomFrom = (_: any, data: any) => {
    const date = data.value as Date | null;
    onChange({
      ...value,
      dateFrom: date ? startOfDayIso(moment(date)) : null,
    });
  };

  const handleCustomTo = (_: any, data: any) => {
    const date = data.value as Date | null;
    onChange({
      ...value,
      dateTo: date ? endOfDayIso(moment(date)) : null,
    });
  };

  return (
    <div>
      <Button.Group size="small" widths="5">
        {PRESETS.map((preset) => (
          <Button
            key={preset.key}
            active={activePreset === preset.key}
            color={activePreset === preset.key ? "green" : undefined}
            onClick={() => onChange(preset.compute())}
          >
            {preset.label}
          </Button>
        ))}
      </Button.Group>
      <Divider />
      <Container fluid>
        <Icon name="calendar alternate outline" color="grey" />
        <span>Custom range:&nbsp;</span>
        <SemanticDatepicker
          locale="en-US"
          type="basic"
          format="YYYY-MM-DD"
          placeholder="From"
          value={value.dateFrom ? new Date(value.dateFrom) : null}
          onChange={handleCustomFrom}
        />
        <SemanticDatepicker
          locale="en-US"
          type="basic"
          format="YYYY-MM-DD"
          placeholder="To"
          value={value.dateTo ? new Date(value.dateTo) : null}
          onChange={handleCustomTo}
        />
      </Container>
    </div>
  );
};

export default MetricPeriodSelector;
